import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import InputMask from 'react-input-mask';
import { CloseSmallIcon, InfoSmallIcon, WarningSmallIcon, EyeIcon, EyeCrossedIcon, ChevronDownIcon } from './utils/IconFactory';
import Dropdown from './Dropdown';
import { isMobile } from './utils';
const Input = forwardRef(({ name, placeholder = '', value, icon = null, rightIcon = null, prefix = undefined, onChange, onFocus, onBlur, onAutoFill, onSelect, type = 'text', size = 'medium', mode = 'normal', selectOnFocus = false, autoFocus = false, readonly, disabled, clearable = false, className = '', mask = '', isTimeControl = false, selectBeginning = false, options, inputmode, detectMobile, searchable, textColor, parentContainerRef, }, ref) => {
    const isMobileDevice = detectMobile
        ? detectMobile()
        : isMobile();
    const containerRef = React.useRef();
    const prefixRef = React.useRef();
    const dropDownRef = React.useRef();
    const timeoutRef = React.useRef(0);
    const [_type, _setType] = React.useState(type);
    const [_options, _setOptions] = React.useState([]);
    const _onChange = (e) => onChange === null || onChange === void 0 ? void 0 : onChange(e.target.value);
    const _clearValue = () => onChange === null || onChange === void 0 ? void 0 : onChange('');
    const [showDropdown, setShowDropdown] = React.useState(false);
    const [dropdownWidth, setDropdownWidth] = React.useState(0);
    const [dropdownHeight, setDropdownHeight] = React.useState(0);
    const [dropdownLeft, setDropdownLeft] = React.useState(0);
    const [dropdownTop, setDropdownTop] = React.useState(0);
    const [prefixWidth, setPrefixWidth] = React.useState(0);
    const [searchStr, setSearchStr] = React.useState('');
    const [searchIndex, setSearchIndex] = React.useState(-1);
    const isAsSelect = React.useMemo(() => Boolean(options), [options]);
    React.useEffect(() => {
        const eventsHandler = (evt) => {
            setShowDropdown(false);
        };
        window.addEventListener('resize', eventsHandler);
        if ((ref === null || ref === void 0 ? void 0 : ref.current) && ref.current.addEventListener) {
            ref.current.addEventListener('animationstart', autoFillHandle, false);
        }
        if (searchable && value && options) {
            const idx = options.findIndex(o => { var _a; return ((_a = o.value) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase()) === value.toLocaleLowerCase(); });
            setSearchIndex(idx);
        }
        if (parentContainerRef && parentContainerRef.current) {
            parentContainerRef.current.addEventListener('scroll', eventsHandler);
        }
        return () => {
            window.removeEventListener('resize', eventsHandler);
            if ((ref === null || ref === void 0 ? void 0 : ref.current) && ref.current.removeEventListener) {
                ref.current.removeEventListener('animationstart', autoFillHandle, false);
            }
            if (parentContainerRef && parentContainerRef.current) {
                parentContainerRef.current.removeEventListener('scroll', eventsHandler);
            }
        };
    }, []);
    React.useEffect(() => {
        _setOptions((options || []).map((option) => {
            const existAction = option.action;
            option.action = (opt) => {
                if (existAction) {
                    existAction(opt);
                }
                else {
                    onChange === null || onChange === void 0 ? void 0 : onChange(opt.value);
                    onSelect === null || onSelect === void 0 ? void 0 : onSelect(opt.value);
                }
            };
            return option;
        }));
    }, [options]);
    const _value = React.useMemo(() => {
        if (!value && value !== 0)
            return '';
        if (!isAsSelect) {
            return value;
        }
        else {
            const selectedValue = options === null || options === void 0 ? void 0 : options.find(({ value: optionValue }) => optionValue == value);
            if (selectedValue) {
                onSelect === null || onSelect === void 0 ? void 0 : onSelect(selectedValue.value);
                return selectedValue.label;
            }
            else {
                return value;
            }
        }
    }, [value, options]);
    const search = (str) => {
        setSearchStr(str);
        if (searchable && str && dropDownRef.current && options) {
            const idx = options.findIndex(o => { var _a; return ((_a = o.label) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase().indexOf(str.toLocaleLowerCase())) === 0; });
            setSearchIndex(idx);
        }
    };
    React.useEffect(() => {
        const changePrefixWidth = () => {
            if (prefixRef.current) {
                const rect = prefixRef.current.getBoundingClientRect();
                setPrefixWidth(rect.width);
            }
            else {
                setPrefixWidth(0);
            }
        };
        changePrefixWidth();
        window.addEventListener('resize', changePrefixWidth);
        return () => {
            window.removeEventListener('resize', changePrefixWidth);
        };
    }, [prefix, prefixRef]);
    const _icon = React.useMemo(() => {
        var _a;
        return !isAsSelect
            ? icon
            : (_a = options === null || options === void 0 ? void 0 : options.find(({ value: optionValue }) => optionValue == value)) === null || _a === void 0 ? void 0 : _a.icon;
    }, [value, icon]);
    const autoFillHandle = ({ target, animationName }) => {
        switch (animationName) {
            case 'onAutoFillStart':
                target.classList.add('is-autofilled');
                break;
            case 'onAutoFillCancel':
                target.classList.remove('is-autofilled');
                break;
        }
    };
    const onContainerClick = () => {
        var _a, _b;
        if (!(options === null || options === void 0 ? void 0 : options.length))
            return;
        const show = !showDropdown;
        if (show) {
            const { width: inputWidth, top: inputTop, bottom: inputBottom, left: inputLeft } = (_a = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            const dropDownHeight = (_b = dropDownRef === null || dropDownRef === void 0 ? void 0 : dropDownRef.current) === null || _b === void 0 ? void 0 : _b.offsetHeight;
            if (!isMobileDevice) {
                setDropdownWidth(inputWidth);
                setDropdownLeft(inputLeft);
                if ((window.innerHeight - inputBottom) < dropDownHeight) {
                    setDropdownTop(inputTop - dropDownHeight - 5);
                }
                else {
                    setDropdownTop(inputBottom);
                }
            }
        }
        setTimeout(() => {
            var _a, _b;
            setShowDropdown(show);
            show && ((_b = (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a));
        }, 10);
    };
    const resetInput = () => {
        if (value && searchable) {
            onChange === null || onChange === void 0 ? void 0 : onChange('');
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(false);
        }
    };
    const props = {
        name,
        value: _value,
        placeholder,
        type,
        inputMode: inputmode,
        sizes: size,
        mode,
        autoFocus,
        readOnly: (isAsSelect || readonly) && !searchable,
        isAsSelect,
        disabled,
        onBlur,
        onKeyDown: (e) => {
            if (searchable && showDropdown) {
                if (searchIndex >= 0) {
                    if (e.keyCode === 13) {
                        onChange === null || onChange === void 0 ? void 0 : onChange(_options[searchIndex].value);
                        setShowDropdown(false);
                    }
                    if (e.keyCode === 38) {
                        setSearchIndex(Math.max(searchIndex - 1, 0));
                    }
                    if (e.keyCode === 40) {
                        setSearchIndex(Math.min(searchIndex + 1, _options.length - 1));
                    }
                }
                if (e.keyCode >= 65 && e.keyCode <= 90) {
                    const char = String.fromCharCode(e.keyCode);
                    search(`${searchStr}${char}`);
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }
                    timeoutRef.current = setTimeout(() => search(''), 1000);
                }
                if (e.keyCode === 27) {
                    if (showDropdown) {
                        if (searchStr) {
                            search('');
                        }
                        else {
                            setShowDropdown(false);
                        }
                    }
                }
                if (e.keyCode === 8) {
                    searchStr && search(searchStr.slice(0, -1));
                }
            }
            if (e.keyCode === 13) {
                onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
            }
            if (e.keyCode === 27) {
                onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
            }
        },
        onFocus: (e) => {
            selectBeginning && e.target.setSelectionRange(0, 0);
            selectOnFocus && e.target.select();
            onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
        }
    };
    return (_jsxs(Container, { className: `${className} ui-input`, ref: containerRef, onClick: onContainerClick, children: [_icon && _jsx(Icon, { className: "input-icon", disabled: props.disabled, children: _icon }), prefix && _jsx(Prefix, { ref: prefixRef, className: "input-prefix", children: prefix }), mask ?
                _jsx(InputMask, { ref: ref, ...{ ...props, type: _type }, maskChar: '_', mask: mask, alwaysShowMask: true, onChange: _onChange, children: (inputProps) => _jsx(StyledInput, { ref: ref, ...inputProps, icon: Boolean(_icon), prefixWidth: prefixWidth, isTimeControl: isTimeControl !== null && isTimeControl !== void 0 ? isTimeControl : false, textColor: textColor }) }) :
                _jsx(StyledInput, { ref: ref, ...{ ...props, type: _type }, isTimeControl: isTimeControl !== null && isTimeControl !== void 0 ? isTimeControl : false, onInput: _onChange, icon: Boolean(_icon), prefixWidth: prefixWidth, textColor: textColor }), mode === 'normal' && clearable && Boolean(_value.length)
                && _jsx(IconContainer, { onClick: _clearValue, disabled: props.disabled, children: _jsx(CloseSmallIcon, {}) }), mode === 'warning' && type !== 'password' && !(options === null || options === void 0 ? void 0 : options.length)
                && _jsx(IconContainer, { mode: mode, disabled: props.disabled, children: _jsx(InfoSmallIcon, {}) }), mode === 'error' && type !== 'password' && !(options === null || options === void 0 ? void 0 : options.length)
                && _jsx(IconContainer, { mode: mode, disabled: props.disabled, children: _jsx(WarningSmallIcon, {}) }), type === 'password' && _type === 'password'
                && _jsx(IconContainer, { mode: mode, onClick: () => _setType('text'), disabled: props.disabled, children: _jsx(EyeIcon, {}) }), type === 'password' && _type === 'text'
                && _jsx(IconContainer, { mode: mode, onClick: () => _setType('password'), disabled: props.disabled, children: _jsx(EyeCrossedIcon, {}) }), rightIcon
                && _jsx(IconContainer, { disabled: props.disabled, children: rightIcon }), isAsSelect && !disabled &&
                _jsxs(_Fragment, { children: [_jsx(IconContainer, { className: "select-chevron-icon", showDropdown: showDropdown, hasCloseIcon: value && searchable, disabled: props.disabled, children: value && searchable ? _jsx(CloseSmallIcon, { onClick: resetInput }) : _jsx(ChevronDownIcon, {}) }), _jsx(Dropdown, { ref: dropDownRef, className: `${className}${isMobileDevice ? 'mobile' : ''}`, options: _options, current: _value, width: dropdownWidth, height: dropdownHeight, top: dropdownTop, left: dropdownLeft, visible: showDropdown, scrollable: true, highlighCurrent: searchable, onClose: () => setShowDropdown(false), searchValue: searchIndex >= 0 ? _options[searchIndex].value : undefined })] })] }));
});
export default Input;
const onFieldAutoFill = keyframes `from {/*  */} to {/*  */}`;
const onFieldAutoFillCancel = keyframes `from {/*  */} to {/*  */}`;
const Container = styled.div `
  position: relative;

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-text-fill-color: var(--text-primary) !important;
    box-shadow: 0 0 0px 1000px var(--ui-input-background) inset;
    transition: background-color 50000s ease-in-out 0s;
  }

  input:-webkit-autofill {
    animation-name: ${onFieldAutoFill};
  }

  input:not(:-webkit-autofill) {
    animation-name: ${onFieldAutoFillCancel};
  }
`;
export const StyledInput = styled.input `
  position: relative;
  appearance: none;
  width: 100%;
  box-sizing: border-box;
  padding-top: var(--ui-input-padding-medium);
  padding-bottom: var(--ui-input-padding-medium);
  padding-left: var(--ui-input-padding-medium);
  padding-right: var(--ui-input-padding-medium);
  background: var(--ui-input-background);
  border: var(--ui-input-border-size) solid var(--ui-input-border-color);
  outline: none;
  border-radius: var(--ui-border-radius-medium);
  font-size: var(--ui-font-size-medium);
  line-height: var(--ui-line-height-medium);
  color: ${props => props.textColor ? props.textColor : `var(--text-primary)`};
  text-align: inherit;
  overflow: hidden;
  text-overflow: ellipsis;

  ${props => props.disabled && css `
    opacity: 0.37;
    cursor: default !important;
  `}

  ${props => props.sizes === 'big' && css `
    padding-top: var(--ui-input-padding-big);
    padding-bottom: var(--ui-input-padding-big);
    padding-left: var(--ui-input-padding-big);
    border-radius: var(--ui-border-radius-big);
    font-size: var(--ui-font-size-big);
    line-height: var(--ui-line-height-big);
  `}

  ${props => props.sizes === 'small' && css `
    padding-top: var(--ui-input-padding-small);
    padding-bottom: var(--ui-input-padding-small);
    padding-left: var(--ui-input-padding-small);
    border-radius: var(--ui-border-radius-small);
    font-size: var(--ui-font-size-small);
    line-height: var(--ui-line-height-small);
  `}

  ${props => !props.disabled && css `
    &:hover {
      background: var(--ui-input-background-hover);
    }
  `}

  &:focus {
    background: var(--ui-input-background-focus);
  }

  ${props => (props.mode === 'normal' && !props.disabled) && css `
    &:hover {
      border-color: var(--ui-input-border-hover);
    }

    &:focus {
      border-color: var(--ui-input-border-focus);
    }
  `}

  ${props => (props === null || props === void 0 ? void 0 : props.mode) === 'error' && css `
    border-color: var(--ui-color-red) !important;
    padding-right: var(--ui-input-mode-padding-right);
  `}

  ${props => (props === null || props === void 0 ? void 0 : props.mode) === 'warning' && css `
    border-color: var(--ui-color-yellow) !important;
    padding-right: var(--ui-input-mode-padding-right);
  `}


  ${props => props.icon && css `
    padding-left: 3em;
  `}

  ${props => props.prefixWidth && css `
    padding-left: calc(0.625rem + ${props.prefixWidth}px + 4px);
  `}

  ${props => props.isTimeControl && css `
    color: var(--text-secondary);

    background-color: var(--element-neutral);
    font-size: inherit;
    border: 1px solid var(--element-neutral);

    &:hover {
      border: 1px solid var(--element-neutral);
    }

    &:focus {
      border: 1px solid var(--element-neutral);
    }
  `}

  ${props => props.isAsSelect && css `
    position: relative;
    padding-right: 2.3rem;
    cursor: pointer;
  `}
`;
const Prefix = styled.div `
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  left: 0.625rem;
  color: var(--text-secondary);
  /* font-weight: 500; */
`;
export const Icon = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;

  margin-left: 0.75em;
  position: absolute;
  left: 0;
  top: 0;

  color: var(--text-secondary);

  & > svg {
    width: 1.5rem;
  }

  ${p => p.disabled && css `
    opacity: 0.37;
  `}
`;
export const IconContainer = styled.div `
  position: absolute;
  width: var(--ui-input-mode-icon-size);
  height: var(--ui-input-mode-icon-size);
  right: var(--ui-input-mode-icon-right);
  top: 50%;
  margin-top: calc(var(--ui-input-mode-icon-size) / 2 * -1);
  color: var(--ui-input-mode-icon-color);
  cursor: pointer;

  svg {
    transition: transform 0.2s ease;
  }

  ${props => props.disabled && css `
    opacity: 0.2;
    cursor: default !important;
  `}

  ${props => props.showDropdown === true && !props.hasCloseIcon && css `
    svg {
      transition: transform 0.2s ease;
      transform: rotate(180deg);
    }
  `}

  ${props => props.mode === 'error' && css `
    color: var(--ui-color-red);
    cursor: default;
  `}

  ${props => props.mode === 'warning' && css `
    color: var(--ui-color-yellow);
    cursor: default;
  `}

`;
